import {Component, EventEmitter, inject, Output, ViewEncapsulation} from '@angular/core';
import {Canvas, Circle, FabricImage, Group, Line, Rect, TDataUrlOptions, Triangle} from "fabric";
import {FormsModule} from "@angular/forms";
import {SliderModule} from "primeng/slider";
import {Button, ButtonDirective} from "primeng/button";
import {DropdownModule} from "primeng/dropdown";
import {DockModule} from "primeng/dock";
import {NgIf} from "@angular/common";
import {DialogModule} from "primeng/dialog";
import {ImageService} from "../../../data/services/image.service";
import {ProgressSpinnerModule} from "primeng/progressspinner";
import {
    Group as SjcGroup,
    ImageDetail,
    ImageEditorImageType,
    InspectionImageEntity
} from "../../../data/types/groups/types";
import {catchError, throwError} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {ImageUploaderService} from "../../../data/services/image-uploader.service";
import {MessageService} from "primeng/api";
import {Make} from "../../../data/types/makes/types";
import {GroupsService} from "../../../data/services/groups.service";


@Component({
    selector: 'app-image-editor',
    standalone: true,
    imports: [
        FormsModule,
        SliderModule,
        ButtonDirective,
        DropdownModule,
        Button,
        DockModule,
        NgIf,
        DialogModule,
        ProgressSpinnerModule
    ],
    templateUrl: './image-editor.component.html',
    styleUrl: './image-editor.component.css',
    encapsulation: ViewEncapsulation.None
})
export class ImageEditorComponent {
    @Output() onImageUploadEvent = new EventEmitter<boolean>()

    imageService = inject(ImageService)
    groupService = inject(GroupsService)
    imageUploader = inject(ImageUploaderService)
    messageService = inject(MessageService)

    private image: InspectionImageEntity | ImageDetail | undefined;
    private group: SjcGroup | undefined;
    private groupSelectedMake: Make | undefined;

    loading: boolean = true;
    uploadingImageLoading: boolean = false;

    canvas!: Canvas;
    dialogVisible = false;
    props = {
        fill: 'red'
    }
    fillColors = ['red', 'orange', 'yellow', 'green'];

    private arrow!: Group;
    private circle!: any;

    private startX!: number;
    private startY!: number;
    isCropped: boolean = false;

    isDrawing = {
        arrow: false,
        circle: false,
    }
    isDrawable = {
        arrow: false,
        circle: false,
    }

    arrowStrokeWidth: number = 14;
    circleStrokeWidth: number = 6;
    ARROW_HEAD_RATIO: number = 3.5;

    originalImage: HTMLImageElement | undefined;
    croppingRect: Rect | undefined;
    scaleFactor: number = 0;
    private imageType: ImageEditorImageType = ImageEditorImageType.SEARCH_IMAGE;


    constructor() {
        this.changeFillOfSelectedObjects = this.changeFillOfSelectedObjects.bind(this);
        this.changeStrokeOfSelectedObjects = this.changeStrokeOfSelectedObjects.bind(this);
        this.changeSelectedObjects = this.changeSelectedObjects.bind(this);
        this.fillObject = this.fillObject.bind(this);
        this.updateStroke = this.updateStroke.bind(this);
    }

    setImageType(type: ImageEditorImageType): void {
        this.imageType = type;
    }

    setImage(image: InspectionImageEntity | ImageDetail) {
        this.image = image;
        this.dialogVisible = true;
    }

    setGroup(group: SjcGroup): void {
        this.group = group;
    }

    setGroupSelectedMake(make: Make) {
        this.groupSelectedMake = make
    }

    showDialog(): void {
        this.loading = true;
        setTimeout(() => {
            this.init()
        }, 100)
    }

    init() {
        this.clearAll()
        this.canvas = new Canvas('fabricCanvas');
        this.addCanvasEvents();
        this.initImage()
    }


    private initImage() {
        if (!this.image) return;

        this.imageService.getImageContent(`https://insp-img.autotechiq.com/webp/${this.image.webp!}`)
            .subscribe({
                next: (blob: Blob) => {
                    const reader = new FileReader();
                    reader.readAsDataURL(blob);
                    reader.onload = (e: any) => {
                        const img = new Image();
                        img.src = e.target.result as string;
                        img.onload = () => {
                            this.originalImage = img;
                            this.loading = false;
                            this.updateCanvasDimensions(img);
                            this.addImageToCanvas(img);
                        };
                    };
                },
                error: (error: any) => console.error('Error loading image', error)
            });
    }

    private updateCanvasDimensions(img: HTMLImageElement): void {
        const maxWidth = 800;
        const aspectRatio = img.width / img.height;
        let canvasWidth = maxWidth;
        let canvasHeight = maxWidth / aspectRatio;

        // Ensure the canvas height does not exceed 600 pixels
        if (canvasHeight > 600) {
            canvasHeight = 600;
            canvasWidth = canvasHeight * aspectRatio;
        }
        this.canvas.setDimensions({width: canvasWidth, height: canvasHeight});
        this.canvas.renderAll();
    }

    addImageToCanvas(img: HTMLImageElement): void {
        const canvasWidth = this.canvas.getWidth();
        const canvasHeight = this.canvas.getHeight();
        const imgWidth = img.width;
        const imgHeight = img.height;

        // Calculate the scale factor
        this.scaleFactor = Math.min(canvasWidth / imgWidth, canvasHeight / imgHeight);

        const scaledWidth = imgWidth * this.scaleFactor;
        const scaledHeight = imgHeight * this.scaleFactor;

        // Create and add the scaled image to the canvas
        const imgInstance = new FabricImage(img, {
            left: (canvasWidth - scaledWidth) / 2,
            top: (canvasHeight - scaledHeight) / 2,
            scaleX: this.scaleFactor,
            scaleY: this.scaleFactor,
            selectable: false,
            evented: false
        });

        this.canvas.add(imgInstance);
        this.canvas.renderAll();
    }

    getDataUrlOfModifiedCanvas(): string | undefined {
        if (!this.originalImage) return

        const objects = this.canvas.getObjects();

        // Remove the scaling from all objects
        objects.forEach((obj) => {
            obj.scaleX = (obj.scaleX || 1) / this.scaleFactor;
            obj.scaleY = (obj.scaleY || 1) / this.scaleFactor;
            obj.left = (obj.left || 0) / this.scaleFactor;
            obj.top = (obj.top || 0) / this.scaleFactor;
            obj.setCoords();
        });

        const originalCanvasWidth = this.canvas.getWidth();
        const originalCanvasHeight = this.canvas.getHeight();

        const [imageWidth, imageHeight] = this.getModifiedCanvasDimensions()

        // Set the canvas to the original image size
        this.canvas.setDimensions({width: imageWidth, height: imageHeight});
        this.canvas.renderAll();

        // Download the canvas as an image
        const dataUrl = this.canvas.toDataURL({
            format: 'png',
            quality: 1,
            width: imageWidth,
            height: imageHeight,
            left: 0,
            top: 0
        } as TDataUrlOptions);

        // Restore the scaling for all objects
        objects.forEach((obj) => {
            obj.scaleX = (obj.scaleX || 1) * this.scaleFactor;
            obj.scaleY = (obj.scaleY || 1) * this.scaleFactor;
            obj.left = (obj.left || 0) * this.scaleFactor;
            obj.top = (obj.top || 0) * this.scaleFactor;
            obj.setCoords();
        });

        // Restore the canvas size
        this.canvas.setDimensions({width: originalCanvasWidth, height: originalCanvasHeight});
        this.canvas.renderAll();
        return dataUrl
    }

    getModifiedCanvasDimensions(): [number, number] {
        if (this.isCropped) {
            return [this.canvas!.getWidth() / this.scaleFactor, this.canvas.getHeight() / this.scaleFactor];
        } else if (this.originalImage) {
            return [this.originalImage.width, this.originalImage.height];
        } else {
            return [0, 0]
        }
    }

    downloadImage() {
        const link = document.createElement('a');
        const dataUrl = this.getDataUrlOfModifiedCanvas();
        link.href = dataUrl!;
        link.download = 'edited-image.png';
        link.click();
    }

    private addCanvasEvents(): void {
        this.canvas.on('mouse:down', (options) => this.onMouseDown(options));
        this.canvas.on('mouse:move', (options) => this.onMouseMove(options));
        this.canvas.on('mouse:up', () => this.onMouseUp());
        this.canvas.on('object:moving', () => {
            this.resetAllDrawing()
        })
    }

    resetAllDrawing(): void {
        this.isDrawing.arrow = false;
        this.isDrawing.circle = false;

        this.isDrawable.arrow = false;
        this.isDrawable.circle = false;
    }

    enableDrawingMode(element: "circle" | "arrow"): void {
        this.isDrawable[element] = true;
        for (const [key, value] of Object.entries(this.isDrawable)) {
            // @ts-ignore
            this.isDrawable[key] = element === key;
        }
    }

    clearAll(): void {
        if (this.canvas) {
            this.clear(this.canvas.getObjects())
            this.croppingRect = undefined;
        }
    }

    clearActiveObject(): void {
        this.clear(this.canvas.getActiveObjects())
    }

    private clear(objects: any[]) {
        objects.forEach((obj) => {
            if (!obj.isType('Image')) {
                this.canvas.remove(obj);
            }
        });
        this.canvas.renderAll();
    }

    private onMouseDown(options: any): void {
        if (this.canvas.getActiveObject()) {
            this.resetAllDrawing();
        }

        if (this.isDrawable.arrow) {
            this.onMouseDownArrow(options)
        } else if (this.isDrawable.circle) {
            this.onMouseDownCircle(options)
        }
    }

    private onMouseDownArrow(options: any) {
        if (!this.isDrawable.arrow && !this.canvas.getActiveObject()) {
            this.isDrawable.arrow = true;
        }

        this.isDrawing.arrow = this.isDrawable.arrow;

        if (!this.isDrawing.arrow) return

        const pointer = this.canvas.getScenePoint(options.e);
        this.startX = pointer.x;
        this.startY = pointer.y;

        this.arrow = this.createArrow(this.startX, this.startY, this.startX, this.startY);
        this.canvas.add(this.arrow);
    }

    private onMouseDownCircle(options: any) {
        if (!this.isDrawable.circle && !this.canvas.getActiveObject()) {
            this.isDrawable.circle = true;
        }

        this.isDrawing.circle = this.isDrawable.circle;

        if (!this.isDrawing.circle) return;

        const pointer = this.canvas.getScenePoint(options.e);
        this.startX = pointer.x;
        this.startY = pointer.y;

        this.circle = new Circle({
            left: this.startX,
            top: this.startY,
            originX: 'center',
            originY: 'center',
            radius: 1,
            selectable: true,
            evented: true,
            strokeWidth: this.circleStrokeWidth,
            hasBorder: true,
            stroke: this.props.fill,
            fill: 'transparent'
        });
        this.canvas.add(this.circle);
    }

    private onMouseMove(options: any): void {
        if (this.isDrawable.arrow) {
            this.onMouseMoveArrow(options)
        } else if (this.isDrawable.circle) {
            this.onMouseMoveCircle(options)
        }
    }

    private onMouseMoveArrow(options: any) {
        if (!this.isDrawing.arrow || !this.arrow) return;

        const pointer = this.canvas.getScenePoint(options.e);
        this.canvas.remove(this.arrow);
        this.arrow = this.createArrow(this.startX, this.startY, pointer.x, pointer.y);
        this.canvas.add(this.arrow);
        this.canvas.renderAll();
    }

    private onMouseMoveCircle(options: any) {
        if (!this.isDrawing.circle || !this.circle) return;

        const pointer = this.canvas.getScenePoint(options.e);
        const radius = Math.sqrt(Math.pow(pointer.x - this.startX, 2) + Math.pow(pointer.y - this.startY, 2));
        this.circle.set({radius: radius});
        this.canvas.renderAll();
    }

    private onMouseUp(): void {
        this.resetAllDrawing();
    }

    private createArrow(x1: number, y1: number, x2: number, y2: number): Group {
        const angle = Math.atan2(y2 - y1, x2 - x1);
        const headlen = this.arrowStrokeWidth * this.ARROW_HEAD_RATIO; // length of head in pixels
        const dx = x2 - x1;
        const dy = y2 - y1;
        const length = Math.sqrt(dx * dx + dy * dy);

        const line = new Line([x1, y1, x2, y2], {
            stroke: this.props.fill,
            strokeWidth: this.arrowStrokeWidth,
            selectable: true,
            evented: true,
        });

        const arrowHead = new Triangle({
            left: x2,
            top: y2,
            originX: 'center',
            originY: 'center',
            angle: angle * 180 / Math.PI + 90,
            width: headlen,
            height: headlen,
            fill: this.props.fill,
            selectable: true,
            evented: true,
        });

        return new Group([line, arrowHead], {
            selectable: true,
            evented: true,
        });
    }

    changeFillOfSelectedObjects() {
        this.changeSelectedObjects(this.fillObject)
    }

    changeStrokeOfSelectedObjects() {
        this.changeSelectedObjects(this.updateStroke)
    }

    changeSelectedObjects(callback: Function) {
        const objects = this.canvas.getActiveObjects();
        for (const object of objects) {
            if (object.isType('Group')) {
                const groupObjects = (object as Group).getObjects()
                for (const groupObject of groupObjects) {
                    callback(groupObject)
                }
            }
            callback(object)
        }
        this.canvas.renderAll();
    }

    fillObject(object: any) {
        object.set('stroke', this.props.fill)

        if (object.isType('Circle')) return

        object.set('fill', this.props.fill)
    }

    updateStroke(object: any) {
        if (object.isType('Circle')) {
            object.set('strokeWidth', this.circleStrokeWidth)
        } else if (object.isType('Line', 'Arrow')) {
            object.set('strokeWidth', this.arrowStrokeWidth)
        } else if (object.isType('Triangle')) {
            const headlen = this.arrowStrokeWidth * this.ARROW_HEAD_RATIO; // length of head in pixels
            object.set('width', headlen)
            object.set('height', headlen)
        }

    }

    close() {
        this.canvas.dispose()
    }

    save() {
        this.uploadingImageLoading = true;
        this.canvas.discardActiveObject()

        this.saveModifiedImage();
    }

    private saveModifiedImage(): void {
        if (this.imageType === ImageEditorImageType.GROUP_IMAGE) {
            this.saveModifiedGroupImage()
        } else if (this.imageType === ImageEditorImageType.SEARCH_IMAGE) {
            this.saveModifiedSearchImage()
        }
    }

    private saveModifiedGroupImage() {
        this.image = this.image as ImageDetail
        const formData = new FormData()
        const dataUrl = this.getDataUrlOfModifiedCanvas()
        const blob = this.imageUploader.dataURLToBlob(dataUrl!)

        formData.append('file', blob, this.image?.webp);
        formData.append('image_id', this.image?.sjc_inspection_image_id!.toString());

        this.groupService.updateModifiedImage(formData).pipe(
            catchError((error) => this.handleError(error))
        ).subscribe(
            (response: any) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Image updated successfully'
                });
                this.onImageUploadEvent.emit(true);

                this.uploadingImageLoading = false;
                this.dialogVisible = false;
            },
        );
    }

    private saveModifiedSearchImage() {
        this.image = this.image as InspectionImageEntity

        const formData = new FormData()
        const dataUrl = this.getDataUrlOfModifiedCanvas()
        const blob = this.imageUploader.dataURLToBlob(dataUrl!)

        formData.append('file', blob, this.image?.filename);
        formData.append('caption', this.image?.caption ?? '');
        formData.append('sjc_group_id', this.group!.id.toString());
        formData.append('sjc_group_name', this.group!.name.toString());

        if (this.groupSelectedMake) {
            formData.append('make_id', this.groupSelectedMake!.id.toString());
        }

        this.imageUploader.uploadImage(formData).pipe(
            catchError((error) => this.handleError(error))
        ).subscribe(
            (response: any) => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'File uploaded successfully'
                });
                this.onImageUploadEvent.emit(true);

                this.uploadingImageLoading = false;
                this.dialogVisible = false;
            },
        );
    }

    handleError(error: HttpErrorResponse) {
        this.messageService.add({severity: 'error', summary: 'Error', detail: 'File upload failed'});
        this.uploadingImageLoading = false;
        return throwError(() => new Error('Something bad happened; please try again later.'));
    }


    cropImage(): void {
        if (!this.croppingRect) {
            console.error('No cropping rectangle found');
            return;
        }

        // Get the original image from the canvas
        const imgObject = this.canvas.getObjects('image')[0] as FabricImage;
        if (!imgObject) {
            console.error('No image found on the canvas');
            return;
        }

        // Calculate the cropping area
        const cropLeft = (this.croppingRect.left || 0) / this.scaleFactor;
        const cropTop = (this.croppingRect.top || 0) / this.scaleFactor;
        const cropWidth = this.croppingRect.getScaledWidth() / this.scaleFactor;
        const cropHeight = this.croppingRect.getScaledHeight() / this.scaleFactor;

        // Get the original image element
        const originalImage = imgObject.getElement() as HTMLImageElement;

        // Create an off-screen canvas for cropping
        const offScreenCanvas = document.createElement('canvas');
        const ctx = offScreenCanvas.getContext('2d')!;
        offScreenCanvas.width = cropWidth;
        offScreenCanvas.height = cropHeight;

        // Draw the cropped area onto the off-screen canvas
        ctx.drawImage(originalImage, cropLeft, cropTop, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);

        // Get the cropped image data
        const croppedDataUrl = offScreenCanvas.toDataURL('image/png');

        // Replace the original image with the cropped image
        const croppedImg = new Image();
        croppedImg.src = croppedDataUrl;
        croppedImg.onload = () => {
            if (!this.croppingRect) {
                console.error('No cropping rectangle found');
                return;
            }

            const croppedFabricImage = new FabricImage(croppedImg, {
                left: imgObject.left,
                top: imgObject.top,
                scaleX: imgObject.scaleX,
                scaleY: imgObject.scaleY,
                selectable: false,
                evented: false
            });



            // Remove the original image and the cropping rectangle from the canvas
            this.canvas.remove(imgObject);
            this.canvas.remove(this.croppingRect);

            // Add the cropped image to the canvas
            this.canvas.add(croppedFabricImage);
            this.canvas.setDimensions({
                width: this.croppingRect.getScaledWidth(),
                height: this.croppingRect.getScaledHeight()
            });

            // Calculate the offset of the cropping rectangle
            const offsetX = this.croppingRect.left || 0;
            const offsetY = this.croppingRect.top || 0;

            // Adjust positions of all objects relative to the new cropped area
            this.canvas.getObjects().forEach((obj) => {
                if (!obj.isType('Image')) {
                    obj.set({
                        left: (obj.left || 0) - offsetX,
                        top: (obj.top || 0) - offsetY
                    });
                    obj.setCoords();
                    this.canvas.bringObjectToFront(obj)
                }
            });

            // Alternatively, move the cropped image to a specific z-index
            // this.canvas.moveTo(croppedFabricImage, desiredZIndex);

            this.canvas.renderAll();
            this.isCropped = true;
            this.croppingRect = undefined;
        };
    }

    createCroppingRect() {
        if (!this.originalImage) return;

        const canvasWidth = this.canvas.getWidth();
        const canvasHeight = this.canvas.getHeight();

        this.croppingRect = new Rect({
            left: (canvasWidth - 100) / 2,
            top: (canvasHeight - 100) / 2,
            width: 100,
            height: 100,
            fill: 'rgba(0,0,0,0.3)',
            stroke: '#ccc',
            strokeDashArray: [5, 5],
            selectable: true,
            evented: true,
            hasBorders: true,
            hasControls: true,
            cornerSize: 6,
            lockRotation: true
        });

        this.canvas.add(this.croppingRect);
        this.canvas.setActiveObject(this.croppingRect);
        this.canvas.renderAll();
    }

    resetToOriginalImage() {
        this.updateCanvasDimensions(this.originalImage!);
        this.addImageToCanvas(this.originalImage!);
        this.clearAll()
        this.isCropped = false;
        this.canvas.renderAll()
    }


    isSaveDisabled() {
        return this.croppingRect
    }
}
